import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Diff } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import {
  useBaseComercial,
  useItensDaBase,
  useServicosDaBase,
  useVersoesDaBase,
} from '../hooks';
import { CodigoVersaoBadge, SituacaoBadge } from '../components/BaseComercialBadges';
import { formatCurrency, formatVigencia } from '../utils/format';
import type { BaseComercial, BaseComercialItem, BaseComercialServico } from '../api';

/**
 * Versões da Base Comercial (task 11.2).
 *
 * Lista as versões do grupo (da mais recente para a mais antiga, com
 * indicação da vigente) e permite comparar duas versões: campos da Base
 * + itens adicionados/removidos/alterados.
 */
export default function BaseComercialVersoesPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: base } = useBaseComercial(id);
  const { data: versoes, isLoading, isError, error } = useVersoesDaBase(id);
  const [aId, setAId] = useState<string>('');
  const [bId, setBId] = useState<string>('');

  // Pré-seleciona a mais recente e a anterior quando carrega.
  const selected = useMemo(() => {
    if (!versoes || versoes.data.length === 0) return { a: '', b: '' };
    const sorted = [...versoes.data];
    const a = sorted.find((v) => v.id === aId) ?? sorted[0];
    const b = sorted.find((v) => v.id === bId) ?? sorted[1] ?? sorted[0];
    return { a: a.id, b: b.id };
  }, [versoes, aId, bId]);

  const aVersao = versoes?.data.find((v) => v.id === selected.a);
  const bVersao = versoes?.data.find((v) => v.id === selected.b);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError || !versoes) {
    return (
      <div className="p-6 space-y-6">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        <Alert variant="error">
          {isError
            ? `Erro ao carregar versões: ${(error as Error)?.message ?? 'Falha desconhecida.'}`
            : 'Versões não encontradas.'}
        </Alert>
      </div>
    );
  }

  const opcoes = versoes.data.map((v) => ({
    value: v.id,
    label: `v${v.versao} • ${v.nome} (${v.situacao})`,
  }));

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>

      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Versões da Base Comercial</h1>
        <p className="mt-1 text-sm text-gray-600">
          {base ? `${base.codigo} — ${base.nome}` : ''} · da mais recente para a mais antiga
        </p>
      </div>

      {/* Lista de versões */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Versão</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Situação</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Vigência</th>
              <th className="px-4 py-2 text-right font-medium text-gray-700">Valor total</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Criada em</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {versoes.data.map((v) => (
              <tr key={v.id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-4 py-3">
                  <CodigoVersaoBadge codigo={v.codigo} versao={v.versao} vigente={v.vigente} />
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <SituacaoBadge situacao={v.situacao} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                  {formatVigencia(v.dataInicio, v.dataFim)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right text-gray-900">
                  {formatCurrency(v.valorTotal, v.moeda)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                  {formatDateTime(v.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Comparação */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
          <Diff className="h-4 w-4" />
          Comparar versões
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <AutocompleteSelect
            label="Versão A (mais antiga)"
            options={opcoes}
            value={selected.a}
            onChange={setAId}
          />
          <AutocompleteSelect
            label="Versão B (mais recente)"
            options={opcoes}
            value={selected.b}
            onChange={setBId}
          />
        </div>
        {aVersao && bVersao && selected.a !== selected.b && (
          <VersaoDiff aId={selected.a} bId={selected.b} />
        )}
        {selected.a === selected.b && (
          <p className="mt-3 text-sm text-gray-500">Selecione duas versões diferentes para comparar.</p>
        )}
      </div>
    </div>
  );
}

function VersaoDiff({ aId, bId }: { aId: string; bId: string }) {
  const { data: aBase } = useBaseComercial(aId);
  const { data: bBase } = useBaseComercial(bId);
  const { data: aItens } = useItensDaBase(aId);
  const { data: bItens } = useItensDaBase(bId);
  const { data: aServicos } = useServicosDaBase(aId);
  const { data: bServicos } = useServicosDaBase(bId);

  if (!aBase || !bBase || !aItens || !bItens || !aServicos || !bServicos) {
    return <Skeleton className="mt-3 h-40 w-full" />;
  }

  const fieldChanges = diffFields(aBase, bBase);

  return (
    <div className="mt-4 space-y-4 text-sm">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Campos alterados
        </div>
        <ul className="mt-1 space-y-1">
          {fieldChanges.length > 0 ? (
            fieldChanges.map((c) => (
              <li key={c.field} className="rounded bg-amber-50 px-2 py-1 text-amber-900">
                <span className="font-medium">{c.label}:</span>{' '}
                <span className="line-through text-amber-700">{c.from}</span>{' '}
                → {c.to}
              </li>
            ))
          ) : (
            <li className="text-gray-500">Nenhum.</li>
          )}
        </ul>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Itens adicionados na versão B
        </div>
        <ul className="mt-1 space-y-1">
          {diffItens(aItens, bItens).added.length > 0 ? (
            diffItens(aItens, bItens).added.map((i) => (
              <li key={i.id} className="rounded bg-green-50 px-2 py-1 text-green-900">
                + {i.professionalProfileSlug} • {i.pricingModelSlug} • {i.quantidade}× {i.valorUnitario}
              </li>
            ))
          ) : (
            <li className="text-gray-500">Nenhum.</li>
          )}
        </ul>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Itens removidos na versão B
        </div>
        <ul className="mt-1 space-y-1">
          {diffItens(aItens, bItens).removed.length > 0 ? (
            diffItens(aItens, bItens).removed.map((i) => (
              <li key={i.id} className="rounded bg-red-50 px-2 py-1 text-red-900">
                − {i.professionalProfileSlug} • {i.pricingModelSlug}
              </li>
            ))
          ) : (
            <li className="text-gray-500">Nenhum.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/**
 * Campos "de topo" da Base cuja mudança entre versões o usuário precisa
 * enxergar. Não inclui campos derivados (valorTotal recomputa a partir
 * dos itens) nem metadata (versão, situação, ids, hashes, datas de
 * auditoria) — o objetivo é mostrar o que o Executivo mudou de propósito.
 */
const FIELD_LABELS: Record<string, string> = {
  nome: 'Nome',
  modalidade: 'Modalidade',
  escopo: 'Escopo',
  dataInicio: 'Data de início',
  dataFim: 'Data final',
  moeda: 'Moeda',
  descontoMaximoPercentual: 'Desconto máximo (%)',
  reajustePeriodicidade: 'Periodicidade de reajuste',
  reajusteIndexSlug: 'Índice de reajuste',
  reajustePercentualFixo: 'Percentual fixo (%)',
  permiteCoexistencia: 'Permite coexistência',
  requerAprovacaoEletronica: 'Exige aprovação eletrônica',
  condicoesFaturamento: 'Condições de faturamento',
  observacoesTributarias: 'Observações tributárias',
  observacoes: 'Observações',
};

function diffFields(
  a: BaseComercial,
  b: BaseComercial,
): Array<{ field: string; label: string; from: string; to: string }> {
  const changes: Array<{ field: string; label: string; from: string; to: string }> = [];
  for (const [field, label] of Object.entries(FIELD_LABELS)) {
    const va = (a as unknown as Record<string, unknown>)[field];
    const vb = (b as unknown as Record<string, unknown>)[field];
    if (va === vb) continue;
    if ((va == null || va === '') && (vb == null || vb === '')) continue;
    changes.push({
      field,
      label,
      from: va == null || va === '' ? '—' : String(va),
      to: vb == null || vb === '' ? '—' : String(vb),
    });
  }
  return changes;
}

function diffItens(
  a: BaseComercialItem[],
  b: BaseComercialItem[],
): { added: BaseComercialItem[]; removed: BaseComercialItem[] } {
  const key = (i: BaseComercialItem) =>
    `${i.professionalProfileSlug}::${i.pricingModelSlug}::${i.quantidade}::${i.valorUnitario}`;
  const bKeys = new Set(b.map(key));
  const aKeys = new Set(a.map(key));
  const added = b.filter((i) => !aKeys.has(key(i)));
  const removed = a.filter((i) => !bKeys.has(key(i)));
  return { added, removed };
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR');
}
